import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  ExpectedRevision,
  FileRepository,
  FileUploadReservation,
  Page,
  PageRequest,
  PersistedFile,
  RepositoryResult,
} from "../contracts";
import type { Database } from "../database.generated";
import type { FileRow, Json } from "../database.types";
import { mapFileRow } from "../mappers";
import { bytesSha256, canonicalJsonSha256 } from "./checksum";
import { decodeCursor, encodeCursor } from "./cursor";
import { mapRepositoryRows, validatePageRequest } from "./repository-base";
import { databaseFailure, notFound, validationFailure } from "./repository-helpers";
import { verifyRenderedFileBytes } from "./rendered-file-verification";

const FILE_ORDER = "id.asc";
const SOURCE_BUCKET = "organisation-source-files";
const RENDERED_BUCKET = "organisation-rendered-reports";
const FIVE_MINUTES_SECONDS = 300;
const MAX_BYTES = 25 * 1024 * 1024;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
const SOURCE_MIME = new Set([
  "application/pdf",
  "image/png",
  "image/jpeg",
  "image/webp",
  "audio/webm",
  "audio/mp4",
  "audio/mpeg",
  "video/webm",
  "video/mp4",
]);
const IMAGE_MIME = new Set(["image/png", "image/jpeg", "image/webp"]);

const scopedOrder = (scope: string) => `${FILE_ORDER}|${scope}`;

const validateUpload = (input: FileUploadReservation): RepositoryResult<true> => {
  if (!input.id.trim() || !input.organisationId.trim() || !input.logicalName.trim() || !input.originalFilename.trim()) {
    return validationFailure("File identity, organisation, logical name and original filename are required.");
  }
  if (input.bytes.byteLength > MAX_BYTES) return validationFailure("File exceeds the 25 MiB limit.");
  if (IMAGE_MIME.has(input.mimeType) && input.bytes.byteLength > MAX_IMAGE_BYTES) {
    return validationFailure("Image exceeds the 10 MiB limit.");
  }
  if (input.bucketId === RENDERED_BUCKET && input.mimeType !== "application/pdf") {
    return validationFailure("Rendered reports must be PDF files.");
  }
  if (input.bucketId === SOURCE_BUCKET && !SOURCE_MIME.has(input.mimeType)) {
    return validationFailure("Source file MIME type is not allowed.");
  }
  if (![SOURCE_BUCKET, RENDERED_BUCKET].includes(input.bucketId)) {
    return validationFailure("Private file bucket is not allowed.");
  }
  if (!input.idempotencyKey.trim()) return validationFailure("File command idempotency key is required.");
  return { ok: true, value: true };
};

export class SupabaseFileRepository implements FileRepository {
  constructor(private readonly client: SupabaseClient<Database>) {}

  async getMetadata(id: string): Promise<RepositoryResult<PersistedFile>> {
    const { data, error } = await this.client.from("files").select("*").eq("id", id).maybeSingle();
    if (error) return databaseFailure(error, "File metadata could not be read.");
    if (!data) return notFound("File");
    return { ok: true, value: mapFileRow(data as FileRow) };
  }

  listForOrganisation(organisationId: string, page: PageRequest) {
    return this.list({ organisationId }, page);
  }

  listForAudit(auditId: string, page: PageRequest) {
    return this.list({ auditId }, page);
  }

  listForReportVersion(reportVersionId: string, page: PageRequest) {
    return this.list({ reportVersionId }, page);
  }

  listForReportSection(reportSectionId: string, page: PageRequest) {
    return this.list({ reportSectionId }, page);
  }

  listExpiredPendingUploads(organisationId: string, page: PageRequest) {
    return this.list({ organisationId, expiredPending: true }, page);
  }

  private async list(
    filter: {
      organisationId?: string;
      auditId?: string;
      reportVersionId?: string;
      reportSectionId?: string;
      expiredPending?: boolean;
    },
    page: PageRequest,
  ): Promise<RepositoryResult<Page<PersistedFile>>> {
    const valid = validatePageRequest(page);
    if (!valid.ok) return valid as RepositoryResult<Page<PersistedFile>>;
    const scope = filter.auditId
      ? `audit:${filter.auditId}`
      : filter.reportVersionId
        ? `report-version:${filter.reportVersionId}`
        : filter.reportSectionId
          ? `report-section:${filter.reportSectionId}`
          : `organisation:${filter.organisationId ?? ""}${filter.expiredPending ? ":expired" : ""}`;
    const order = scopedOrder(scope);
    let after: string | undefined;
    if (page.cursor) {
      const decoded = decodeCursor(page.cursor, order, 1);
      if (!decoded.ok) return decoded as RepositoryResult<Page<PersistedFile>>;
      if (typeof decoded.value.values[0] !== "string") return validationFailure("File cursor is invalid.");
      after = decoded.value.values[0];
    }
    let query = this.client.from("files").select("*").order("id", { ascending: true }).limit(page.limit + 1);
    if (filter.organisationId) query = query.eq("organisation_id", filter.organisationId);
    if (filter.auditId) query = query.eq("audit_engagement_id", filter.auditId);
    if (filter.reportVersionId) query = query.eq("report_version_id", filter.reportVersionId);
    if (filter.reportSectionId) query = query.eq("report_section_id", filter.reportSectionId);
    if (filter.expiredPending) {
      query = query.eq("status", "Pending").lt("upload_expires_at", new Date().toISOString());
    }
    if (after) query = query.gt("id", after);
    const { data, error } = await query;
    if (error) return databaseFailure(error, "Files could not be listed.");
    const rows = (data ?? []) as FileRow[];
    const hasNext = rows.length > page.limit;
    const visible = hasNext ? rows.slice(0, page.limit) : rows;
    const mapped = mapRepositoryRows(visible, mapFileRow);
    if (!mapped.ok) return mapped;
    return {
      ok: true,
      value: {
        items: mapped.value,
        nextCursor: hasNext && visible.length
          ? encodeCursor({ version: 1, order, values: [visible[visible.length - 1].id] })
          : undefined,
      },
    };
  }

  async reserveUpload(input: FileUploadReservation): Promise<RepositoryResult<PersistedFile>> {
    const valid = validateUpload(input);
    if (!valid.ok) return valid;
    const sha256 = await bytesSha256(input.bytes);
    const payload = {
      organisationId: input.organisationId,
      fileId: input.id,
      auditEngagementId: input.auditEngagementId ?? "",
      reportVersionId: input.reportVersionId ?? "",
      reportSectionId: input.reportSectionId ?? "",
      evidenceRecordId: input.evidenceRecordId ?? "",
      logicalName: input.logicalName,
      originalFilename: input.originalFilename,
      bucketId: input.bucketId,
      mimeType: input.mimeType,
      sizeBytes: input.bytes.byteLength,
      sha256,
      versionNumber: input.versionNumber ?? 1,
      predecessorFileId: input.predecessorFileId ?? "",
      visibility: input.visibility ?? "Internal only",
      metadataSchemaVersion: input.metadata?.schemaVersion ?? 1,
      metadata: input.metadata ?? { schemaVersion: 1, data: {} },
      idempotencyKey: input.idempotencyKey,
    } satisfies Json;
    const result = await this.command("reserve_file_upload", payload);
    if (!result.ok) return result as RepositoryResult<PersistedFile>;
    return this.mapCommandFile(result.value);
  }

  async uploadReservedObject(file: PersistedFile, bytes: Uint8Array): Promise<RepositoryResult<PersistedFile>> {
    if (file.status !== "Pending") return validationFailure("Only a pending file can be uploaded.");
    if (bytes.byteLength !== file.sizeBytes || await bytesSha256(bytes) !== file.sha256) {
      return validationFailure("Upload bytes do not match the reserved size and checksum.");
    }
    const { error } = await this.client.storage.from(file.bucketId).upload(file.objectPath, bytes, {
      contentType: file.mimeType,
      upsert: false,
      metadata: {
        organisationId: file.organisationId,
        fileId: file.id,
        sha256: file.sha256,
      },
    });
    if (error) return databaseFailure(error, "The reserved object could not be uploaded.");
    return { ok: true, value: file };
  }

  async confirmUpload(
    file: PersistedFile,
    bytes: Uint8Array,
    idempotencyKey: string,
  ): Promise<RepositoryResult<PersistedFile>> {
    if (file.status !== "Pending") return validationFailure("Only a pending file can be confirmed.");
    if (file.bucketId === RENDERED_BUCKET) {
      const verification = await verifyRenderedFileBytes(this.client, file.id);
      if (!verification.ok) return verification as RepositoryResult<PersistedFile>;
    } else {
      const { data, error } = await this.client.storage.from(file.bucketId).download(file.objectPath);
      if (error || !data) return databaseFailure(error, "The uploaded object could not be verified.");
      if (data.type && data.type !== file.mimeType) {
        return this.reconcile(file, "Quarantined", idempotencyKey);
      }
      const stored = new Uint8Array(await data.arrayBuffer());
      if (stored.byteLength !== file.sizeBytes || stored.byteLength !== bytes.byteLength) {
        return this.reconcile(file, "Quarantined", idempotencyKey);
      }
      const storedChecksum = await bytesSha256(stored);
      if (storedChecksum !== file.sha256 || storedChecksum !== await bytesSha256(bytes)) {
        return this.reconcile(file, "Quarantined", idempotencyKey);
      }
    }
    const result = await this.command("confirm_file_upload", {
      organisationId: file.organisationId,
      fileId: file.id,
      expectedRevision: file.revision,
      objectEtag: "",
      idempotencyKey,
    });
    if (!result.ok) return result as RepositoryResult<PersistedFile>;
    return this.mapCommandFile(result.value);
  }

  async createDownloadGrant(id: string): Promise<RepositoryResult<{ url: string; expiresAt: string }>> {
    const file = await this.getMetadata(id);
    if (!file.ok) return file;
    if (!["Available", "Archived"].includes(file.value.status)) {
      return validationFailure("Only an available or archived file can be downloaded.");
    }
    if (file.value.bucketId === RENDERED_BUCKET) {
      return validationFailure(
        "Rendered reports require the trusted presentation diagnostic download service.",
      );
    }
    const { data, error } = await this.client.storage
      .from(file.value.bucketId)
      .createSignedUrl(file.value.objectPath, FIVE_MINUTES_SECONDS);
    if (error || !data?.signedUrl) return databaseFailure(error, "A signed download URL could not be created.");
    return {
      ok: true,
      value: {
        url: data.signedUrl,
        expiresAt: new Date(Date.now() + FIVE_MINUTES_SECONDS * 1000).toISOString(),
      },
    };
  }

  async archive(id: string, options: ExpectedRevision, idempotencyKey: string) {
    const file = await this.getMetadata(id);
    if (!file.ok) return file;
    const result = await this.command("archive_file", {
      organisationId: file.value.organisationId,
      fileId: id,
      expectedRevision: options.expectedRevision,
      idempotencyKey,
    });
    if (!result.ok) return result as RepositoryResult<PersistedFile>;
    return this.mapCommandFile(result.value);
  }

  async abandonPendingUpload(
    id: string,
    options: ExpectedRevision,
    idempotencyKey: string,
  ): Promise<RepositoryResult<PersistedFile>> {
    const file = await this.getMetadata(id);
    if (!file.ok) return file;
    const abandoned = await this.command("abandon_staged_upload", {
      organisationId: file.value.organisationId,
      fileId: id,
      expectedRevision: options.expectedRevision,
      idempotencyKey,
    });
    if (!abandoned.ok) return abandoned as RepositoryResult<PersistedFile>;
    const mapped = this.mapCommandFile(abandoned.value);
    if (!mapped.ok) return mapped;
    const { error } = await this.client.storage.from(mapped.value.bucketId).remove([mapped.value.objectPath]);
    if (error) return databaseFailure<PersistedFile>(error, "The abandoned object could not be removed.");
    const removed = await this.command("reconcile_file_object", {
      organisationId: mapped.value.organisationId,
      fileId: mapped.value.id,
      expectedRevision: mapped.value.revision,
      outcome: "Object removed",
      idempotencyKey: `${idempotencyKey}:removed`,
    });
    if (!removed.ok) return removed as RepositoryResult<PersistedFile>;
    return this.mapCommandFile(removed.value);
  }

  async reconcileObjectState(id: string, idempotencyKey: string): Promise<RepositoryResult<PersistedFile>> {
    const file = await this.getMetadata(id);
    if (!file.ok) return file;
    const { data, error } = await this.client.storage.from(file.value.bucketId).download(file.value.objectPath);
    if (error || !data) {
      if (["Available", "Archived"].includes(file.value.status)) {
        return this.reconcile(file.value, "Missing", idempotencyKey);
      }
      return databaseFailure(error, "The reserved object is unavailable.");
    }
    if (data.type && data.type !== file.value.mimeType) {
      return this.reconcile(file.value, "Quarantined", idempotencyKey);
    }
    const stored = new Uint8Array(await data.arrayBuffer());
    if (stored.byteLength !== file.value.sizeBytes || await bytesSha256(stored) !== file.value.sha256) {
      return this.reconcile(file.value, "Quarantined", idempotencyKey);
    }
    return { ok: true, value: file.value };
  }

  async finaliseReplacement(
    replacementId: string,
    expectedReplacementRevision: number,
    expectedPredecessorRevision: number,
    idempotencyKey: string,
  ): Promise<RepositoryResult<{ replacement: PersistedFile; predecessor: PersistedFile }>> {
    const replacement = await this.getMetadata(replacementId);
    if (!replacement.ok) return replacement;
    const result = await this.command("finalise_file_replacement", {
      organisationId: replacement.value.organisationId,
      replacementFileId: replacementId,
      expectedReplacementRevision,
      expectedPredecessorRevision,
      idempotencyKey,
    });
    if (!result.ok) return result as RepositoryResult<{ replacement: PersistedFile; predecessor: PersistedFile }>;
    const value = result.value as Record<string, unknown>;
    return {
      ok: true,
      value: {
        replacement: mapFileRow(value.replacement as FileRow),
        predecessor: mapFileRow(value.predecessor as FileRow),
      },
    };
  }

  private async reconcile(
    file: PersistedFile,
    outcome: "Missing" | "Quarantined",
    idempotencyKey: string,
  ): Promise<RepositoryResult<PersistedFile>> {
    const result = await this.command("reconcile_file_object", {
      organisationId: file.organisationId,
      fileId: file.id,
      expectedRevision: file.revision,
      outcome,
      idempotencyKey,
    });
    if (!result.ok) return result as RepositoryResult<PersistedFile>;
    return this.mapCommandFile(result.value);
  }

  private async command(name: FileCommandName, payload: Json): Promise<RepositoryResult<Json>> {
    const requestChecksum = await canonicalJsonSha256(payload);
    const { data, error } = await this.client.rpc(name, {
      p_request: payload,
      p_request_checksum: requestChecksum,
    });
    if (error) return databaseFailure(error, `The ${name} file command failed.`);
    return { ok: true, value: data as Json };
  }

  private mapCommandFile(value: Json): RepositoryResult<PersistedFile> {
    const record = value as Record<string, unknown>;
    if (!record.file) return validationFailure("File command returned an invalid result.");
    return { ok: true, value: mapFileRow(record.file as FileRow) };
  }
}

type FileCommandName =
  | "reserve_file_upload"
  | "confirm_file_upload"
  | "abandon_staged_upload"
  | "reconcile_file_object"
  | "archive_file"
  | "finalise_file_replacement";

export const fileRepositoryLimits = {
  maxBytes: MAX_BYTES,
  maxImageBytes: MAX_IMAGE_BYTES,
  signedUrlSeconds: FIVE_MINUTES_SECONDS,
} as const;
